// spineStore — a tiny reactive, localStorage-backed store for the well spine
// (FieldPad ─< Well ─< Wellbore ─< Job) plus PadJob campaigns.
//
// Bore→Well cutover Phase 2 GROUNDWORK (facade-over-spine). This module makes the spine's localStorage
// a shared, SUBSCRIBABLE source: a module-level store per key + `useSyncExternalStore`, so a write in
// one component re-renders every component that SUBSCRIBES (i.e. reads the reactive `pads`/`wells`/
// `padJobs` field the spine hooks expose). Before this, each spine hook kept a private
// `useState(readStore())`, so cross-component writes did not propagate.
//
// This is the FOUNDATION for the later facade step, where `useBorefieldData` becomes a read-only
// Borefield view over the spine (via `padToBorefieldView`) — never a second writable model (epic §6
// Sev-1). That facade flip is DEFERRED (it re-points the timeline agent's Gate-A consumers) and is NOT
// wired here — today `useBorefieldData` still reads `borefieldSlice`. NOTE: a component that captures a
// mount snapshot via the getters (`getPads()`/`getWells()`/…) rather than reading the reactive field
// keeps its prior snapshot behavior (no auto-update); only reactive-field readers get live updates.
//
// WHY NOT REDUX (deferred D2): keeping the store as plain localStorage + useSyncExternalStore means no
// Provider is required, so the spine's store-free test suites keep working. The Redux `wellsSlice`
// (ratified D2) is deferred until the backend API lands (its devtools/RTK-Query parity pays off then).
//
// KEYS ARE UNCHANGED (`oxbo-pads-v1` / `oxbo-wells-v1` / `oxbo-pad-jobs-v1`) so pre-cutover demo/wizard
// data loads without migration (Charter Absolute #7 — no silent destruction). NO engineering/economic/
// statistical math (Charter Absolute #2) — pure state container.

import type { Pad, Well } from "../types";
import type { PadJob } from "../../jobs/types";
import {
  createReactiveLocalStore,
  type ReactiveLocalStore,
} from "../../../lib/reactiveLocalStore";
import { createOverlayStore } from "../../../lib/overlayStore";
import { CORPUS_MODE } from "./corpusMode";
import { fetchCorpusPads, fetchCorpusWells } from "./corpusApi";

export const PADS_STORAGE_KEY = "oxbo-pads-v1";
export const WELLS_STORAGE_KEY = "oxbo-wells-v1";
export const PAD_JOBS_STORAGE_KEY = "oxbo-pad-jobs-v1";

// The reactive-localStorage factory moved to lib/ once completions became a second consumer (P3a
// review, Sev-1 — a feature must not import another feature's infra). Re-exported here so the spine's
// existing importers keep their `./spineStore` path.
export { createReactiveLocalStore };
export type { ReactiveLocalStore };

// CORPUS MODE (PB-04 Inc 2). Pads and wells can be backed by the read-only corpus that
// `blueprints/wells.py` serves, with local authoring layered on top (`lib/overlayStore.ts`). The
// PUBLIC SURFACE IS IDENTICAL — `read`/`setAll`/`subscribe` — so `usePadData`, `useWellData`,
// `borefieldFacade` and the rest of the spine's consumers are untouched by the swap. That is the
// whole reason the spine was built on `useSyncExternalStore` rather than a private `useState`:
// the primitive already tolerates a store that starts empty, fetches, and then notifies, so
// CONSUMERS DO NOT BECOME ASYNC.
//
// WHY THIS SOLVES PB-01/PB-02. The corpus stops being written to localStorage at all — the browser
// holds a ~590-byte index row per well instead of the 631 KB aggregate
// (`python3 scripts/measure_corpus_api.py --only index`) — so the ~5 MB origin quota goes back to
// authored records (`oxbo-afes-v1`, `oxbo-cellars-v1`, …), which is what was being starved when an
// AFE save failed (BACKLOG PB-01).
//
// PAD JOBS STAY LOCAL: the corpus has no PadJob concept (campaigns are authored, not extracted),
// so there is nothing to overlay and its store is unchanged in both modes.
export const padsStore: ReactiveLocalStore<Pad> = CORPUS_MODE
  ? createOverlayStore<Pad>(PADS_STORAGE_KEY, fetchCorpusPads)
  : createReactiveLocalStore<Pad>(PADS_STORAGE_KEY);

export const wellsStore: ReactiveLocalStore<Well> = CORPUS_MODE
  ? createOverlayStore<Well>(WELLS_STORAGE_KEY, fetchCorpusWells)
  : createReactiveLocalStore<Well>(WELLS_STORAGE_KEY);

export const padJobsStore =
  createReactiveLocalStore<PadJob>(PAD_JOBS_STORAGE_KEY);
